import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Preloader from '../components/Preloader';
import ScrollToTop from '../components/ScrollToTop';
import PageTitle from '../components/PageTitle';
import { WOW } from 'wowjs';
import 'animate.css';

function Counter({ stop, suffix = '', label }) {
  const [count, setCount] = React.useState(0);
  const [started, setStarted] = React.useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setStarted(true); obs.disconnect(); } }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let cur = 0;
    const inc = stop / (2400 / 16);
    const t = setInterval(() => {
      cur += inc;
      if (cur >= stop) { setCount(stop); clearInterval(t); }
      else setCount(Math.floor(cur));
    }, 16);
    return () => clearInterval(t);
  }, [started, stop]);

  return (
    <div className="counter-block col-lg-3 col-sm-6" ref={ref}>
      <div className="counter-inner">
        <div className="counter-number">
          <span className="count-num">{count.toLocaleString()}</span>
          <span className="count-suffix">{suffix}</span>
        </div>
        <div className="counter-label">{label}</div>
      </div>
    </div>
  );
}

const features = [
  {
    img: '/images/facilities/restaurant.jpg',
    title: 'Our Restaurant',
    sub: 'Authentic Dining',
    desc: 'Our in-house restaurant serves a mouth-watering selection of authentic Nigerian dishes and continental meals. From rich egusi soup and ofe onugbu to grilled catfish and jollof rice, every meal is freshly prepared by our experienced chefs using quality local ingredients.',
  },
  {
    img: '/images/facilities/bar-lounge.jpg',
    title: 'Bar & Games Lounge',
    sub: 'Premium Relaxation',
    desc: 'Unwind at our elegantly appointed bar and games lounge. We stock an extensive selection of premium spirits, wines, beers and cocktails. Challenge friends to a game of pool or simply relax in our comfortable lounge seating while watching live sports on screen.',
  },
  {
    img: '/images/facilities/outdoor-terrace.jpg',
    title: 'Outdoor Terrace',
    sub: 'Open-Air Comfort',
    desc: 'Our beautifully landscaped outdoor terrace features a lush green artificial lawn with colourful modern seating under a shaded canopy — a perfect spot for al-fresco dining, social gatherings, or simply breathing fresh air after a long day.',
  },
];

export default function About() {
  useEffect(() => {
    const wow = new WOW({ mobile: false, live: true });
    wow.init();
  }, []);

  return (
    <div className="page-wrapper">
      <Preloader />
      <Header />
      <PageTitle title="About Us" breadcrumbs={[{ label: 'About Manchester Villa Hotel' }]} />

      {/* About Intro */}
      <section className="about-section">
        <div className="auto-container">
          <div className="row align-items-center">
            <div className="col-lg-6 order-lg-2 wow fadeInRight" data-wow-delay="200ms">
              <div className="home-about-content">
                <div className="sec-title">
                  <span className="sub-title">Who We Are</span>
                  <h2>Manchester Villa Hotel — Owerri's Premier Hospitality Destination</h2>
                  <p className="text">Located at Plot 34, Area T, behind Apams Casket, off PH Road, Owerri, Imo State, Manchester Villa Hotel stands as one of the finest boutique hotels in the South-East of Nigeria. We pride ourselves on delivering world-class hospitality rooted in the warmth and culture of Imo State.</p>
                  <p className="text">Whether you are visiting for business, leisure or a special occasion, our dedicated team ensures every moment of your stay is comfortable, memorable and exceeds your expectations.</p>
                </div>
                <ul className="about-check-list">
                  <li><i className="fas fa-circle-check"></i> 24-hour power supply with backup generators</li>
                  <li><i className="fas fa-circle-check"></i> Round-the-clock security and reception service</li>
                  <li><i className="fas fa-circle-check"></i> Free high-speed WiFi throughout the property</li>
                  <li><i className="fas fa-circle-check"></i> Spacious secure parking for all guests</li>
                  <li><i className="fas fa-circle-check"></i> In-house restaurant, bar, lounge &amp; games room</li>
                </ul>
                <Link to="/contact" className="theme-btn btn-style-one mt-3"><span className="btn-title">Book a Stay</span></Link>
              </div>
            </div>
            <div className="col-lg-6 wow fadeInLeft">
              <div className="home-about-images">
                <figure className="img-main"><img src="/images/exterior/front-view.jpg" alt="Manchester Villa Hotel Exterior" /></figure>
                <figure className="img-inset"><img src="/images/exterior/side-view.jpg" alt="Manchester Villa Hotel Side View" /></figure>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="marquee-section">
        <div className="marquee">
          {[0, 1].map(g => (
            <div key={g} className="marquee-group">
              <div className="text text-style2">Manchester Villa Hotel</div>
              <span className="text divider"><i className="fas fa-star"></i></span>
              <div className="text">Owerri, Imo State</div>
              <span className="text divider"><i className="fas fa-star"></i></span>
              <div className="text text-style2">Premium Hospitality</div>
              <span className="text divider"><i className="fas fa-star"></i></span>
              <div className="text">Nigeria's Finest</div>
              <span className="text divider"><i className="fas fa-star"></i></span>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="feature-section-two pb-0">
        <div className="auto-container">
          {features.map((f, i) => (
            <div key={i} className="row feature-row g-0 align-items-stretch mb-0">
              {i % 2 === 0 ? (
                <>
                  <div className="col-lg-6">
                    <figure style={{ margin: 0, height: '100%', overflow: 'hidden' }}>
                      <img src={f.img} alt={f.title} style={{ width: '100%', height: '100%', minHeight: 380, objectFit: 'cover' }} />
                    </figure>
                  </div>
                  <div className="col-lg-6">
                    <div className="feature-section-two content-box">
                      <div className="sec-title">
                        <span className="sub-title">{f.sub}</span>
                        <h2>{f.title}</h2>
                        <p>{f.desc}</p>
                      </div>
                      <Link to="/services" className="theme-btn btn-style-two read-more">READ MORE</Link>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-lg-6">
                    <div className="feature-section-two content-box">
                      <div className="sec-title">
                        <span className="sub-title">{f.sub}</span>
                        <h2>{f.title}</h2>
                        <p>{f.desc}</p>
                      </div>
                      <Link to="/services" className="theme-btn btn-style-two read-more">READ MORE</Link>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <figure style={{ margin: 0, height: '100%', overflow: 'hidden' }}>
                      <img src={f.img} alt={f.title} style={{ width: '100%', height: '100%', minHeight: 380, objectFit: 'cover' }} />
                    </figure>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="counter-section" style={{ padding: '70px 0' }}>
        <div className="counter-bg"></div>
        <div className="auto-container">
          <div className="row">
            <Counter stop={7}    suffix="+"  label="Room Categories" />
            <Counter stop={500}  suffix="+"  label="Happy Guests" />
            <Counter stop={24}   suffix="/7" label="Reception & Security" />
            <Counter stop={4}    suffix=""   label="Rate Tiers Available" />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonial-section-five pt-80 pb-80">
        <div className="auto-container">
          <div className="sec-title text-center wow fadeInUp">
            <span className="sub-title">Guest Reviews</span>
            <h2>What Our Guests Say</h2>
          </div>
          <div className="row">
            {[
              { name: 'Chukwuemeka Obi', role: 'Business Traveller', text: 'Manchester Villa Hotel made my business trip to Owerri absolutely seamless. The rooms are spotless, the food is exceptional and the staff are incredibly warm and attentive. Highly recommended!' },
              { name: 'Ngozi Adaeze', role: 'Weekend Guest', text: 'I celebrated my anniversary here and it was magical. The suite was beautiful, the bar was fully stocked and the outdoor terrace is just perfect for a relaxed evening. Will definitely return!' },
              { name: 'Bello Ismaila', role: 'Corporate Client', text: 'Stayed here for a week while on a corporate assignment. The 24-hour power supply and WiFi made working from the room very convenient. Breakfast was always fresh and delicious.' },
            ].map((t, i) => (
              <div key={i} className="testimonial-block-five col-lg-4 col-sm-6">
                <div className="inner-box">
                  <div className="content-box">
                    <i className="fas fa-quote-right" style={{ fontSize: 28, color: 'var(--theme-color1)', opacity: 0.25, float: 'right' }}></i>
                    <p className="text">{t.text}</p>
                  </div>
                  <div className="info-box">
                    <div className="thumb-placeholder" style={{ width: 50, height: 50, borderRadius: '50%', background: 'var(--theme-color1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>
                      {t.name[0]}
                    </div>
                    <div>
                      <span className="designation">{t.role}</span>
                      <div className="name">{t.name}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
